import { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import {
  Container,
  Paper,
  TextInput,
  PasswordInput,
  Button,
  Stack,
  Text,
  Alert,
  Checkbox,
  Box,
  PinInput,
  Center,
  Anchor,
  Group,
  ActionIcon,
} from "@mantine/core";
import { IconAlertCircle, IconCheck, IconX } from "@tabler/icons-react";
import { getCurrentUser, signOut } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { useQueryClient } from "@tanstack/react-query";
import { resolveHomePortal } from "@livingsyncs/permissions";
import type { PortalId } from "@livingsyncs/types";
import {
  useConfirmPasswordReset,
  useRequestPasswordReset,
  useLogin,
  useReauthProfiles,
  describeReauthDenial,
  describePinLoginError,
} from "../hooks/useAuth";
import { setAuthPersistence } from "../lib/authPersistence";
import { isNative } from "../lib/platform";
import { isReauthClientConfigured } from "../lib/cognitoClients";
import { MfaSetup } from "../components/auth/MfaSetup";
import { NewPasswordRequired } from "../components/auth/NewPasswordRequired";
import {
  PORTAL_LABEL,
  wrongPortalMessage,
  readAndClearWrongPortalHome,
} from "../lib/crossAppRedirect";
import api, { clearCapturedCredentials } from "../lib/apiClient";
import { withDeadline } from "../lib/withDeadline";
import livingsyncLogoUrl from "../assets/livingsync-logo.svg";

// Upper bound on each of evaluateSession()'s two awaits — see its comment.
// Same 10s budget as the credential mint (MINT_TIMEOUT_MS in useAuth.ts).
const SESSION_CHECK_TIMEOUT_MS = 10_000;

interface LoginPageProps {
  /** Which app this is — shown near the top ("Admin Portal", etc.) and
   * compared against the signed-in account's home portal. A mismatch
   * (e.g. a platform admin signing in here at the app portal) signs them
   * out immediately and shows an inline error naming the right portal,
   * rather than silently carrying the session over to it. */
  ownPortal: PortalId;
}

export function LoginPage({ ownPortal }: LoginPageProps) {
  const location = useLocation();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [awaitingSession, setAwaitingSession] = useState(false);
  // Set either by our own post-sign-in portal check below, or by a Boundary
  // component (AdminBoundary et al) that redirected here after finding an
  // existing session no longer belongs to this portal. Router state is
  // checked first, sessionStorage second — a Boundary's own <Navigate>
  // races PrivateRoute's own bare redirect reacting to the same signOut()
  // Hub event, and whichever the router applies last wins the router-state
  // slot; the storage channel is what actually guarantees delivery
  // regardless of that race (see usePortalGuard's doc for the full story).
  const [wrongPortalHome, setWrongPortalHome] = useState<PortalId | null>(
    // Lazy initializer — readAndClearWrongPortalHome() mutates
    // sessionStorage (read-then-clear), so it must run at most once on
    // mount, not on every re-render this component happens to take.
    () =>
      (location.state as { wrongPortalHome?: PortalId } | null)
        ?.wrongPortalHome ?? readAndClearWrongPortalHome(),
  );
  /*
   * F-130 — recovery for someone who cannot sign in.
   *
   * "request" asks Cognito to email a code; "confirm" spends it. Local to this
   * page rather than a route, so a half-finished reset cannot be linked to or
   * bookmarked, and leaving the page abandons it cleanly.
   */
  const [resetStage, setResetStage] = useState<"none" | "request" | "confirm">(
    "none",
  );
  const [resetCode, setResetCode] = useState("");
  const [resetPasswordValue, setResetPasswordValue] = useState("");
  const [resetNotice, setResetNotice] = useState<string | null>(null);

  // US-176 Phase 2 — lock-screen fast re-auth. Cached profiles are
  // device-local (never a server roster query, see reauthProfileCache.ts)
  // and only ever offered on a native shell (mobile/desktop) — plain web
  // always shows the full email+password form (spec decision: PIN re-auth
  // is a shared-cart/kiosk feature, not a general web shortcut).
  const { profiles: reauthProfiles, forget: forgetReauthProfile } =
    useReauthProfiles();
  const [pinTargetEmail, setPinTargetEmail] = useState<string | null>(null);
  const [useFullForm, setUseFullForm] = useState(false);
  const [pinResetKey, setPinResetKey] = useState(0);
  // A build without the PIN re-auth app client can't sign anyone in by PIN
  // (lib/cognitoClients.ts), so a profile cached by an earlier build must not
  // lead to a PIN screen whose only possible outcome is an error.
  const hasCachedProfiles =
    isNative && isReauthClientConfigured() && reauthProfiles.length > 0;
  const showProfilePicker =
    hasCachedProfiles && !useFullForm && !pinTargetEmail;
  const showPinEntry = hasCachedProfiles && !useFullForm && !!pinTargetEmail;

  const {
    mutate: login,
    isPending,
    error,
    authStep,
    totpSetupUri,
    confirmNewPassword,
    isNewPasswordPending,
    confirmMfa,
    isConfirmPending,
    loginWithPin,
    isPinLoginPending,
    pinLoginError,
    pinSetupPhase,
    pinSetupBlockedReason,
    setPin,
    isSetPinPending,
    setPinError,
    skipPinSetupForNow,
  } = useLogin();
  const [pinSetupResetKey, setPinSetupResetKey] = useState(0);

  const requestReset = useRequestPasswordReset();
  const confirmReset = useConfirmPasswordReset();

  // Check initial session and listen for sign-in events. A session (fresh
  // sign-in, or one already present on mount) is only valid HERE if its
  // account's home portal is this app — otherwise sign out immediately and
  // show an inline error naming the right portal, rather than silently
  // carrying it over (that was the earlier cross-app-handoff behavior; the
  // wrong portal is now treated as an invalid sign-in, not a redirect).
  useEffect(() => {
    let cancelled = false;

    async function evaluateSession() {
      // Review fix (cycle 3, Blocker) — both awaits below are bounded. With
      // no timeout (the axios instance sets none), a stalled connection left
      // "Signing you in…" up forever with nothing to tap — the same C-6
      // violation cycle 1 fixed for the PIN-setup screen, via a second path.
      // A deadline takes the SAME branch a failure already takes: an
      // unresolvable getCurrentUser() means "not signed in" (back to the
      // form), and an unresolvable /auth/me means "let them through" (the
      // existing catch below — PrivateRoute and the portal boundaries
      // re-derive the real context on the next render regardless).
      try {
        await withDeadline(getCurrentUser(), SESSION_CHECK_TIMEOUT_MS);
      } catch {
        if (!cancelled) setIsAuthenticated(false);
        return;
      }
      try {
        const { data } = await withDeadline(
          api.get("/auth/me"),
          SESSION_CHECK_TIMEOUT_MS,
        );
        const home = resolveHomePortal(data.data);
        if (home !== "no-access" && home !== ownPortal) {
          // Cleared here too, not just in usePortalGuard's own equivalent
          // branch — this device may be shared/kiosk (iPad in a group
          // home), and a forced sign-out must not leave the account that
          // just got bounced visible via a stale query cache to whoever
          // signs in next.
          queryClient.clear();
          clearCapturedCredentials();
          await signOut();
          if (!cancelled) {
            setWrongPortalHome(home);
            setIsAuthenticated(false);
          }
          return;
        }
      } catch {
        // Backend check failed (e.g. a transient network error) — fall
        // through and let it through; PrivateRoute/the app's own boundary
        // re-derives the real context on the next render regardless.
      }
      if (!cancelled) setIsAuthenticated(true);
    }

    evaluateSession();

    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signedIn") {
        setWrongPortalHome(null);
        // A sign-in just completed and this check decides where to send the
        // user — show "Signing you in…" rather than the forms until it does.
        // Without this, skipping the PIN step while this check was still in
        // flight flashed the email/password form at someone already signed
        // in. Cleared whichever way the check lands (redirect, or wrong-
        // portal sign-out back to the form with its alert).
        setAwaitingSession(true);
        void evaluateSession().finally(() => {
          if (!cancelled) setAwaitingSession(false);
        });
      }
      if (payload.event === "signedOut") {
        setAwaitingSession(false);
        setIsAuthenticated(false);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [ownPortal, queryClient]);

  // US-176 Phase 2 follow-up — hold the redirect while a just-completed full
  // sign-in is still deciding (or showing) the forced "set your PIN" step.
  //
  // Review fix (cycle 1) — this ordering claim is now VERIFIED against the
  // installed @aws-amplify/auth source (6.20.0), not just asserted. Password
  // sign-in resolves via signInWithSRP.mjs, which does
  // `await dispatchSignedInHubEvent()` and only `return`s AFTER — so the
  // 'signedIn' Hub event this file's OWN evaluateSession() listens for has
  // ALREADY fired by the time our own `signIn()` call resolves. That means
  // evaluateSession() gets a head start on STARTING, but it still needs its
  // own separate `getCurrentUser()` call plus a real network round-trip to
  // `/auth/me` before it can call `setIsAuthenticated(true)`. Our own path
  // (useLogin's mintIfDone, called from the SAME onSuccess this signIn()
  // resolution triggers) needs zero further network hops — it sets
  // `pinSetupState({phase:"checking"})` synchronously. A few synchronous
  // microtasks will always finish before a real HTTP round-trip, regardless
  // of exact runtime scheduling — this is a structural guarantee from the
  // path-length difference, not a timing coincidence. Regression-tested end
  // to end (real Hub, real useLogin) in
  // lib/__tests__/pinSetupRace.integration.test.tsx — this codebase has a
  // documented history of this exact race class going wrong (see
  // crossAppRedirect.integration.test.tsx's own header), so this claim does
  // not get to stand on a comment alone twice.
  if (isAuthenticated === true && !pinSetupPhase) {
    return <Navigate to="/" replace />;
  }

  const isValidEmail = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const emailError =
    email && !isValidEmail(email) ? "Invalid email format" : null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // HIPAA: choose token persistence BEFORE signing in. Unchecked = session-only
    // (tokens cleared when the tab/browser closes — safe for shared devices);
    // checked = persist across restarts. No-op on desktop (OS keychain).
    setAuthPersistence(rememberMe);
    login({ email, password });
  };

  return (
    <Container size={420} my={100}>
      <Paper withBorder shadow="md" p={30} radius="md">
        <Box ta="center" mb="md">
          <img
            src={livingsyncLogoUrl}
            alt="LivingSyncs"
            style={{ height: "48px", width: "auto", margin: "0 auto" }}
          />
        </Box>
        <Text size="sm" fw={600} c="teal" ta="center" mb={4}>
          {PORTAL_LABEL[ownPortal]}
        </Text>
        <Text size="xs" ta="center" c="dimmed" mb="xs" fs="italic">
          Empowering Health, Enriching Lives!
        </Text>
        <Text size="sm" ta="center" c="dimmed" mb="xl">
          Group Home Management System
        </Text>

        {wrongPortalHome && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="orange"
            title="Wrong portal"
            role="alert"
            mb="md"
          >
            {wrongPortalMessage(wrongPortalHome)}
          </Alert>
        )}

        {/* Admin-invited user must replace their temp Cognito password */}
        {authStep === "new_password_required" && (
          <NewPasswordRequired
            email={email}
            onSubmit={confirmNewPassword}
            isPending={isNewPasswordPending}
            error={error as Error | null}
          />
        )}

        {/* TOTP first-time setup */}
        {authStep === "mfa_setup" && totpSetupUri && (
          <MfaSetup
            totpSetupUri={totpSetupUri}
            onSubmit={confirmMfa}
            isPending={isConfirmPending}
            error={error as Error | null}
          />
        )}

        {/* TOTP code entry (returning users) */}
        {authStep === "mfa_required" && (
          <Stack gap="md">
            <Text size="sm" fw={500}>
              Two-factor authentication
            </Text>
            <Text size="sm" c="dimmed">
              Enter the 6-digit code from your authenticator app.
            </Text>

            {error && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                title="Invalid code"
              >
                {(error as Error).message ||
                  "Incorrect code. Please try again."}
              </Alert>
            )}

            {/* Mantine's PinInput doesn't accept `autoComplete`; the
                individual `<input>`s underneath already get `inputMode=numeric`
                via the `type="number"` prop, which is what mobile keyboards
                care about for SMS/TOTP autofill. */}
            {/* Centred: a Stack stretches its children, so the boxes sat
                left of the heading and links above them (seen on the iPad,
                2026-09-20). */}
            <Center>
              <PinInput
                length={6}
                type="number"
                inputMode="numeric"
                autoFocus
                onComplete={confirmMfa}
              />
            </Center>

            <Button
              fullWidth
              size="md"
              loading={isConfirmPending}
              onClick={() => {
                /* handled by PinInput onComplete */
              }}
            >
              Verify
            </Button>
          </Stack>
        )}

        {/* F-130 — password recovery. Cognito's own flow: request a code to a
            verified email, then set a new password with it. The pool has
            allowed this all along; nothing surfaced it. */}
        {authStep === "idle" && resetStage !== "none" && (
          <Stack gap="md">
            {resetNotice && (
              <Alert icon={<IconCheck size={16} />} color="blue" role="status">
                {resetNotice}
              </Alert>
            )}
            {(requestReset.error || confirmReset.error) && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                title="Could not reset your password"
                role="alert"
              >
                {((requestReset.error ?? confirmReset.error) as Error).message}
              </Alert>
            )}

            <TextInput
              label="Email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-label="Email address"
            />

            {resetStage === "confirm" && (
              <>
                <TextInput
                  label="Verification code"
                  description="Sent to your email address."
                  required
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  aria-label="Verification code"
                />
                <PasswordInput
                  label="New password"
                  required
                  autoComplete="new-password"
                  value={resetPasswordValue}
                  onChange={(e) => setResetPasswordValue(e.target.value)}
                  aria-label="New password"
                />
              </>
            )}

            <Button
              fullWidth
              size="md"
              loading={requestReset.isPending || confirmReset.isPending}
              disabled={
                !email ||
                (resetStage === "confirm" &&
                  (!resetCode || !resetPasswordValue))
              }
              onClick={() => {
                if (resetStage === "request") {
                  requestReset.mutate(email, {
                    onSuccess: () => {
                      setResetNotice(
                        "If that address has an account, a verification code is on its way.",
                      );
                      setResetStage("confirm");
                    },
                  });
                } else {
                  confirmReset.mutate(
                    { email, code: resetCode, newPassword: resetPasswordValue },
                    {
                      onSuccess: () => {
                        setResetNotice(
                          "Password updated. Sign in with your new password.",
                        );
                        setResetStage("none");
                        setResetCode("");
                        setResetPasswordValue("");
                      },
                    },
                  );
                }
              }}
            >
              {resetStage === "request" ? "Send code" : "Set new password"}
            </Button>

            <Anchor
              component="button"
              type="button"
              size="sm"
              ta="center"
              onClick={() => {
                setResetStage("none");
                setResetNotice(null);
              }}
            >
              Back to sign in
            </Anchor>
          </Stack>
        )}

        {/* US-176 Phase 2 follow-up — the forced first-PIN-set step, shown
            right after a full sign-in mints a device credential when this
            employee has no PIN on file yet, or tells the user plainly why
            it couldn't be set up. Never blocks getting into the app (spec
            C-6's own principle, applied to PIN setup too) — every branch
            below has a way through. */}
        {/* A completed sign-in is being routed (see awaitingSession) —
            never show the sign-in forms to someone who is already in. */}
        {authStep === "idle" &&
          resetStage === "none" &&
          !pinSetupPhase &&
          awaitingSession && (
            <Stack gap="xs" align="center">
              <Text size="sm" c="dimmed" ta="center" role="status">
                Signing you in…
              </Text>
              {/* Bounded by SESSION_CHECK_TIMEOUT_MS anyway, but a blocking
                  screen never gets to be a dead end. "Continue" takes the
                  same path a failed /auth/me check already takes: into the
                  app, where PrivateRoute and the portal boundaries re-check
                  the session themselves. Nothing is bypassed — this page's
                  check is a routing convenience, not an access control. */}
              <Anchor
                component="button"
                type="button"
                size="sm"
                onClick={() => {
                  setAwaitingSession(false);
                  setIsAuthenticated(true);
                }}
              >
                Continue
              </Anchor>
            </Stack>
          )}

        {authStep === "idle" && resetStage === "none" && pinSetupPhase && (
          <Stack gap="md">
            {pinSetupPhase === "checking" && (
              <>
                <Text size="sm" c="dimmed" ta="center">
                  Checking this device…
                </Text>
                {/* Review fix (cycle 1, Critical) — mintReauthCredentialIfPossible
                    now bounds itself to ~10s (MINT_TIMEOUT_MS), but a caregiver
                    on a shared device shouldn't have to wait even that long
                    with nothing to do. Never leave a blocking screen with no
                    way through, even one that resolves on its own. */}
                <Anchor
                  component="button"
                  type="button"
                  size="sm"
                  ta="center"
                  onClick={skipPinSetupForNow}
                >
                  Skip for now
                </Anchor>
              </>
            )}

            {pinSetupPhase === "blocked" && (
              <>
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="yellow"
                  role="alert"
                  title="Fast sign-in isn't available on this device"
                >
                  {/* Per-reason guidance from the backend's ReauthDenialCode.
                      Previously every refusal said "enable two-factor
                      authentication", which was wrong for someone who just
                      hadn't checked in yet. */}
                  {!pinSetupBlockedReason || pinSetupBlockedReason === "failed"
                    ? "Something went wrong setting this up. You can continue signing in normally, or try again later."
                    : `${describeReauthDenial(pinSetupBlockedReason)} You can continue signing in normally.`}
                </Alert>
                <Button fullWidth size="md" onClick={skipPinSetupForNow}>
                  Continue
                </Button>
              </>
            )}

            {pinSetupPhase === "setup" && (
              <>
                <Text size="sm" fw={500} ta="center">
                  Set up a PIN for faster sign-in
                </Text>
                <Text size="sm" c="dimmed" ta="center">
                  Next time, you can sign in on this device with just your PIN
                  instead of your full password.
                </Text>

                {setPinError && (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    color="red"
                    title="Couldn't set that PIN"
                    role="alert"
                  >
                    {(setPinError as Error).message ||
                      "Something went wrong. Please try again."}
                  </Alert>
                )}

                {/* Centred: a Stack stretches its children, so the boxes sat
                    left of the heading and links above them (seen on the iPad,
                    2026-09-20). */}
                <Center>
                  <PinInput
                    key={pinSetupResetKey}
                    length={6}
                    type="number"
                    inputMode="numeric"
                    mask
                    autoFocus
                    disabled={isSetPinPending}
                    onComplete={(pin) =>
                      setPin(pin, {
                        onError: () => setPinSetupResetKey((k) => k + 1),
                      })
                    }
                  />
                </Center>

                <Anchor
                  component="button"
                  type="button"
                  size="sm"
                  ta="center"
                  onClick={skipPinSetupForNow}
                >
                  Skip for now
                </Anchor>
              </>
            )}
          </Stack>
        )}

        {/* US-176 Phase 2 — device-local profile picker (mobile/desktop only,
            never plain web). Replaces typing email+password with a tap +
            PIN when this device already holds a re-auth credential for at
            least one profile. */}
        {authStep === "idle" &&
          resetStage === "none" &&
          !pinSetupPhase &&
          !awaitingSession &&
          showProfilePicker && (
            <Stack gap="md">
              <Text size="sm" fw={500} ta="center">
                Who&apos;s signing in?
              </Text>
              {reauthProfiles.map((p) => (
                <Group key={p.email} gap="xs" wrap="nowrap">
                  <Button
                    variant="light"
                    fullWidth
                    size="md"
                    onClick={() => setPinTargetEmail(p.email)}
                  >
                    {p.displayName || p.email}
                  </Button>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="lg"
                    aria-label={`Remove ${p.displayName || p.email} from this device`}
                    onClick={() => forgetReauthProfile(p.email)}
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </Group>
              ))}
              <Anchor
                component="button"
                type="button"
                size="sm"
                ta="center"
                onClick={() => setUseFullForm(true)}
              >
                Sign in with a different account
              </Anchor>
            </Stack>
          )}

        {/* US-176 Phase 2 — PIN entry for the profile picked above. A single
            mutation (loginWithPin): the device signs the CUSTOM_AUTH
            challenge automatically, so there is no intermediate authStep to
            render the way the password path has. */}
        {authStep === "idle" &&
          resetStage === "none" &&
          !pinSetupPhase &&
          !awaitingSession &&
          showPinEntry && (
            <Stack gap="md">
              <Text size="sm" fw={500} ta="center">
                {reauthProfiles.find((p) => p.email === pinTargetEmail)
                  ?.displayName ?? pinTargetEmail}
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                Enter your PIN
              </Text>

              {pinLoginError && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="red"
                  title={describePinLoginError(pinLoginError).title}
                  role="alert"
                >
                  {describePinLoginError(pinLoginError).message}
                </Alert>
              )}

              {/* Centred: a Stack stretches its children, so the boxes sat
                  left of the heading and links above them (seen on the iPad,
                  2026-09-20). */}
              <Center>
                <PinInput
                  key={pinResetKey}
                  length={6}
                  type="number"
                  inputMode="numeric"
                  mask
                  autoFocus
                  disabled={isPinLoginPending}
                  onComplete={(pin) => {
                    if (!pinTargetEmail) return;
                    loginWithPin(
                      { email: pinTargetEmail, pin },
                      { onError: () => setPinResetKey((k) => k + 1) },
                    );
                  }}
                />
              </Center>

              {/* Disabled boxes alone read as a frozen screen. The links
                  below are disabled for the same span: leaving mid-attempt
                  started a password sign-in on top of a PIN one still in
                  flight (Amplify holds ONE global sign-in state). The attempt
                  is bounded (PIN_SIGN_IN_TIMEOUT_MS), so they come back. */}
              {isPinLoginPending && (
                <Text size="sm" c="dimmed" ta="center" role="status">
                  Signing you in…
                </Text>
              )}

              <Anchor
                component="button"
                type="button"
                size="sm"
                ta="center"
                disabled={isPinLoginPending}
                onClick={() => setPinTargetEmail(null)}
              >
                Not you?
              </Anchor>
              <Anchor
                component="button"
                type="button"
                size="sm"
                ta="center"
                disabled={isPinLoginPending}
                onClick={() => {
                  setEmail(pinTargetEmail ?? "");
                  setUseFullForm(true);
                }}
              >
                Use password instead
              </Anchor>
            </Stack>
          )}

        {/* Standard email/password form — always available on web; on a
            native shell, shown when there's no cached profile yet or the
            user explicitly asked for it from the picker/PIN screen above. */}
        {authStep === "idle" &&
          resetStage === "none" &&
          !pinSetupPhase &&
          !awaitingSession &&
          (!hasCachedProfiles || useFullForm) && (
            <form onSubmit={handleSubmit}>
              <Stack gap="md">
                {error && (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    color="red"
                    title="Login failed"
                    role="alert"
                  >
                    {(error as Error).message ||
                      "Invalid email or password. Please try again."}
                  </Alert>
                )}

                <TextInput
                  label="Email"
                  placeholder="your@email.com"
                  type="email"
                  required
                  autoFocus
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={emailError}
                  rightSection={
                    email && isValidEmail(email) ? (
                      <IconCheck size={16} color="green" />
                    ) : null
                  }
                  aria-label="Email address"
                />

                <PasswordInput
                  label="Password"
                  placeholder="Your password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-label="Password"
                />

                <Checkbox
                  label="Remember me"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.currentTarget.checked)}
                />

                <Button
                  type="submit"
                  fullWidth
                  size="md"
                  loading={isPending}
                  disabled={!!emailError || !email || !password}
                  aria-label="Sign in to LivingSyncs"
                >
                  {isPending ? "Signing in..." : "Sign in"}
                </Button>

                <Anchor
                  component="button"
                  type="button"
                  size="sm"
                  ta="center"
                  onClick={() => {
                    setResetNotice(null);
                    setResetStage("request");
                  }}
                >
                  Forgot your password?
                </Anchor>

                <Text size="xs" c="dimmed" ta="center">
                  Press <kbd>Enter</kbd> to sign in
                </Text>

                {hasCachedProfiles && useFullForm && (
                  <Anchor
                    component="button"
                    type="button"
                    size="sm"
                    ta="center"
                    onClick={() => {
                      setUseFullForm(false);
                      setPinTargetEmail(null);
                    }}
                  >
                    Back to profile picker
                  </Anchor>
                )}
              </Stack>
            </form>
          )}

        <Text size="xs" c="dimmed" mt="xl" ta="center">
          Need help? Contact your system administrator
        </Text>
      </Paper>
    </Container>
  );
}
